import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  ClipboardList,
  X,
  Trash2,
  CheckCircle2,
  ArrowRightLeft,
  Search,
  PlusCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { RecycledChevron } from '@/hooks/useRecycledLockedTickers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
/**
 * AdminPendingNonDividendReviewButton — Round 95z (Jun 2026).
 *
 * Mirrors `AdminPendingDividendReviewButton` for the non-dividend side
 * of the Non-Dividend Stock Expansion (Section 1c).
 *
 * Lists rows from `pending_non_dividend_review` populated by the daily
 * `non-div-pending-review-scanner` cron. The admin picks one of three
 * actions per row:
 *   🗑️ Trash       — hard delete from queue (per user spec)
 *   ✅ Accept      — hydrate into stock_cache as non-div
 *   🔁 Move to Div — transfer to existing pending_dividend_review queue
 *
 * Search field filters by ticker substring. The "Visible" count in the
 * header reflects the search-filtered set so the admin sees the impact
 * of typing immediately.
 */
export default function AdminPendingNonDividendReviewButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hideHasFinancials, setHideHasFinancials] = useState(false);
  // Round P112a (Jul 2026) — bulk-accept selection state. Mirrors
  // AdminPendingDividendReviewButton's "Add Checked" flow: `selected`
  // is a Set of tickers, master checkbox selects the currently-visible
  // (search-filtered) rows, and Accept Checked iterates alphabetically
  // through the selection calling the existing single-ticker accept
  // endpoint serially. Failures per ticker are toasted and skipped
  // (same predictable "add what we can, skip the rest" contract).
  const [selected, setSelected] = useState(new Set());
  const [bulkAccepting, setBulkAccepting] = useState(false);
  // Round P122 (Aug 2026) — Manual add. Type any ticker → smart add:
  // fresh hydrate if not in cache, or MOVE from div → non-div (Data
  // Hygiene cleanup + flip pays_dividend) if it's currently a div row.
  const [manualTicker, setManualTicker] = useState('');
  const [manualAdding, setManualAdding] = useState(false);

  const visibleCandidates = useMemo(() => {
    const all = report?.candidates || [];
    const q = searchQuery.trim().toUpperCase();
    let filtered = all;
    if (q) filtered = filtered.filter((c) => (c.ticker || '').toUpperCase().includes(q));
    if (hideHasFinancials) {
      // Show ONLY tickers Massive has zero financials for — guaranteed thin candidates
      filtered = filtered.filter((c) => c.has_massive_financials === false);
    }
    return filtered;
  }, [report, searchQuery, hideHasFinancials]);

  // Round P112a — derived selection state. Master checkbox is checked
  // iff every currently-visible row is already in `selected`.
  const allVisibleSelected =
    visibleCandidates.length > 0 &&
    visibleCandidates.every((c) => selected.has(c.ticker));

  const toggleOne = (ticker) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(ticker)) n.delete(ticker);
      else n.add(ticker);
      return n;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) {
        visibleCandidates.forEach((c) => n.delete(c.ticker));
      } else {
        visibleCandidates.forEach((c) => n.add(c.ticker));
      }
      return n;
    });
  };

  if (user?.role !== 'admin') return null;

  const fetchList = async () => {
    setLoading(true);
    setReport(null);
    setOpen(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/pending-non-dividend-review`,
        { withCredentials: true }
      );
      setReport(data);
    } catch (err) {
      toast.error('Could not load non-div review list', {
        description: err?.response?.data?.detail || err?.message,
      });
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const removeFromState = (ticker) => {
    setReport((prev) =>
      prev
        ? {
            ...prev,
            candidates: (prev.candidates || []).filter(
              (c) => c.ticker !== ticker
            ),
            count: Math.max(0, (prev.count || 1) - 1),
          }
        : prev
    );
  };

  const trash = async (ticker) => {
    setBusyTicker(ticker);
    try {
      await axios.delete(
        `${API}/admin/pending-non-dividend-review/${encodeURIComponent(ticker)}`,
        { withCredentials: true }
      );
      removeFromState(ticker);
      toast.success(`Trashed ${ticker}`);
    } catch (err) {
      toast.error(`Could not trash ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const accept = async (ticker) => {
    setBusyTicker(ticker);
    try {
      const { data } = await axios.post(
        `${API}/admin/pending-non-dividend-review/${encodeURIComponent(ticker)}/accept`,
        null,
        { withCredentials: true }
      );
      // Round 98q (Jul 2026) — Endpoint now returns 200 with
      // `{ok: false, reason, detail}` for the "not in Massive" case
      // so Cloudflare doesn't strip our friendly message. Surface
      // the detail as an error toast, but leave the row in the
      // queue so admin can retry later or trash it manually.
      if (data && data.ok === false) {
        toast.error(`Could not accept ${ticker}`, {
          description: data.detail || 'Massive returned no data for this ticker',
        });
        return;
      }
      removeFromState(ticker);
      toast.success(`Accepted ${ticker}`, {
        description: data?.name ? `Hydrated as ${data.name}` : undefined,
      });
    } catch (err) {
      toast.error(`Could not accept ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Round P112a (Jul 2026) — Bulk accept. Iterates over checked
  // tickers in alphabetical order, calls the existing single-ticker
  // accept endpoint serially. On {ok:false} (Massive not-in-index) we
  // count as skipped and leave the row in queue; on hard failure we
  // toast and continue. Mirrors the AdminPendingDividendReviewButton
  // bulk-promote pattern EXACTLY for consistency.
  const bulkAccept = async () => {
    const tickers = [...selected].sort();
    if (tickers.length === 0) {
      toast.error('No tickers selected');
      return;
    }
    setBulkAccepting(true);
    const tally = { ok: 0, fail: 0, notInMassive: 0 };
    for (const tk of tickers) {
      setBusyTicker(tk);
      try {
        const { data } = await axios.post(
          `${API}/admin/pending-non-dividend-review/${encodeURIComponent(tk)}/accept`,
          null,
          { withCredentials: true }
        );
        if (data && data.ok === false) {
          tally.notInMassive += 1;
          toast.error(`Skipped ${tk}`, {
            description: data.detail || 'Massive returned no data',
            duration: 3500,
          });
          continue;
        }
        tally.ok += 1;
        toast.success(`Accepted ${tk}`, {
          description: data?.name ? `Hydrated as ${data.name}` : undefined,
          duration: 3000,
        });
        removeFromState(tk);
        setSelected((prev) => {
          const n = new Set(prev);
          n.delete(tk);
          return n;
        });
      } catch (err) {
        tally.fail += 1;
        toast.error(`Skipped ${tk}`, {
          description: err?.response?.data?.detail || err?.message || 'Accept failed',
          duration: 4000,
        });
      }
    }
    setBusyTicker(null);
    setBulkAccepting(false);
    toast.success(
      `Bulk accept complete — ${tally.ok} hydrated, ${tally.notInMassive} not-in-Massive, ${tally.fail} errored`,
      { duration: 5500 }
    );
  };


  const moveToDividend = async (ticker) => {
    setBusyTicker(ticker);
    try {
      await axios.post(
        `${API}/admin/pending-non-dividend-review/${encodeURIComponent(ticker)}/move-to-dividend`,
        null,
        { withCredentials: true }
      );
      removeFromState(ticker);
      toast.success(`Moved ${ticker} → Pending Dividend Review`);
    } catch (err) {
      toast.error(`Could not move ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  // Round P122 (Aug 2026) — Manual add. Smart handler:
  //   • Not in cache → hydrate fresh as non-div (Massive ticker details).
  //   • Already in cache as non-div → 409 toast "already in non-div DB".
  //   • Already in cache as DIVIDEND → MOVE div → non-div (Data Hygiene
  //     cleanup + flip pays_dividend=False). Uses the new
  //     `/admin/manual-add-non-dividend/{t}` endpoint which handles all
  //     three cases server-side.
  const manualOnboard = async () => {
    const t = (manualTicker || '').trim().toUpperCase();
    if (!t) {
      toast.error('Enter a ticker symbol first');
      return;
    }
    if (!/^[A-Z][A-Z.\-]{0,9}$/.test(t)) {
      toast.error(`"${t}" doesn't look like a valid ticker`);
      return;
    }
    setManualAdding(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/manual-add-non-dividend/${encodeURIComponent(t)}`,
        {},
        { withCredentials: true }
      );
      if (data && data.ok === false) {
        toast.error(`Could not add ${t}`, {
          description: data.detail || 'Upstream failure — see server logs',
          duration: 8000,
        });
        return;
      }
      const wasMove = !!data.moved_from_div;
      toast.success(wasMove ? `Moved ${t} to non-dividend DB` : `Added ${t} as non-dividend`, {
        description: data.name || undefined,
        duration: 6000,
      });
      setManualTicker('');
      // Drop from the local list if it happened to be in the pending
      // non-div queue too.
      removeFromState(t);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 409) {
        toast.error(`${t} is already in the non-dividend DB`, { duration: 6000 });
      } else if (status === 400) {
        toast.error(`Cannot add ${t}`, { description: detail, duration: 8000 });
      } else {
        toast.error(`Could not add ${t}`, { description: detail, duration: 8000 });
      }
    } finally {
      setManualAdding(false);
    }
  };

  const fmtMcap = (m) => {
    if (m == null || Number.isNaN(m)) return '—';
    if (m >= 1e9) return `$${(m / 1e9).toFixed(2)}B`;
    if (m >= 1e6) return `$${(m / 1e6).toFixed(1)}M`;
    return `$${m.toLocaleString()}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={fetchList}
        data-testid="admin-pending-non-div-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-purple-500/15 hover:bg-purple-500/25 text-purple-900 dark:text-purple-200 border border-purple-500/40 transition-colors"
        title="Review newly-detected non-dividend tickers waiting to be accepted, trashed, or moved to the dividend review queue"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ClipboardList className="w-4 h-4" />
        )}
        Pending Non-Div Review
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setOpen(false)}
          data-testid="admin-pending-non-div-modal"
        >
          <div
            className="relative w-full max-w-3xl max-h-[85vh] flex flex-col bg-card border border-purple-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">
                  Pending Non-Dividend Review
                </h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Tickers classified as non-dividend by the daily cron, not yet
                  in stock_cache. Trash, accept (hydrate), or move to the
                  dividend review queue.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="admin-pending-non-div-close"
                className="p-1 rounded hover:bg-muted/40"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Search bar + counts */}
            <div className="flex items-center gap-3 px-4 py-2 border-b border-border/60 bg-muted/20 shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search ticker…"
                  data-testid="admin-pending-non-div-search"
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md bg-background border border-border/60 focus:outline-none focus:border-purple-500/60"
                />
              </div>
              <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">
                Visible:{' '}
                <span className="font-bold text-foreground">
                  {visibleCandidates.length}
                </span>{' '}
                / Total:{' '}
                <span className="font-bold text-foreground">
                  {report?.count ?? '—'}
                </span>
              </span>
              <button
                onClick={() => setHideHasFinancials((v) => !v)}
                data-testid="admin-pending-non-div-filter-no-financials"
                title="Show only tickers Massive has no financial data for — guaranteed thin candidates if accepted"
                className={`text-[10px] font-bold px-2 py-1 rounded border transition-colors ${
                  hideHasFinancials
                    ? 'bg-amber-500/25 text-amber-200 border-amber-500/60'
                    : 'bg-muted/30 text-muted-foreground border-border/60 hover:bg-muted/50'
                }`}
              >
                {hideHasFinancials ? '⚠ no-financials only' : 'show all'}
              </button>
            </div>

            {/* Round P112a (Jul 2026) — Bulk-accept toolbar. Master
                checkbox selects every currently-visible row; Accept
                Checked iterates alphabetically calling the same
                per-row accept endpoint. */}
            <div className="flex items-center gap-3 px-4 py-2 border-b border-border/60 bg-muted/10 shrink-0">
              <label className="flex items-center gap-2 text-[11px] text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) {
                      const someChecked = visibleCandidates.some((c) => selected.has(c.ticker));
                      el.indeterminate = !allVisibleSelected && someChecked;
                    }
                  }}
                  onChange={toggleAllVisible}
                  disabled={bulkAccepting || visibleCandidates.length === 0}
                  className="w-3.5 h-3.5 accent-purple-500 cursor-pointer"
                  data-testid="admin-pending-non-div-master-checkbox"
                  title={allVisibleSelected ? 'Uncheck all visible' : 'Check all visible'}
                />
                <span>Select all visible</span>
              </label>
              <span className="flex-1" />
              <button
                onClick={bulkAccept}
                disabled={bulkAccepting || selected.size === 0}
                data-testid="admin-pending-non-div-bulk-accept"
                title="Accept every checked ticker — hydrates each into stock_cache as a non-dividend row. Failures are toasted and skipped."
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {bulkAccepting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-3 h-3" />
                )}
                Accept Checked ({selected.size})
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-3">
              {/* Round P122 (Aug 2026) — Manual add row (always visible
                  even when queue is empty). Smart-handles fresh
                  hydrate + move-from-div in one endpoint. */}
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-2.5 mb-3" data-testid="pending-non-div-manual-add">
                <div className="flex items-center gap-2 mb-1.5">
                  <PlusCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-300" />
                  <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-200">Manual add</div>
                  <div className="text-[10px] text-muted-foreground italic ml-auto hidden sm:block">
                    Add any ticker, or move a div → non-div (auto Data Hygiene).
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={manualTicker}
                    onChange={(e) => setManualTicker(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !manualAdding) manualOnboard(); }}
                    placeholder="Type ticker (e.g. RVII)…"
                    className="flex-1 px-2.5 py-1.5 text-xs rounded-md border border-border bg-background font-mono uppercase tracking-wider focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    data-testid="pending-non-div-manual-input"
                    disabled={manualAdding}
                    maxLength={10}
                  />
                  <button
                    type="button"
                    onClick={manualOnboard}
                    disabled={manualAdding || !manualTicker.trim()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    title="Hydrate this ticker into the non-dividend DB. If it's currently in the dividend DB, moves it (strips dividend fields + wipes dividend history)."
                    data-testid="pending-non-div-manual-add-btn"
                  >
                    {manualAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlusCircle className="w-3 h-3" />}
                    Add ticker
                  </button>
                </div>
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading review queue…
                </div>
              )}

              {!loading && report?.count === 0 && (
                <div className="p-6 text-center">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500/70 mb-2" />
                  <p className="text-sm font-medium text-foreground">
                    Queue is empty
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    No new non-dividend tickers waiting for review. The cron
                    runs daily — check back tomorrow.
                  </p>
                </div>
              )}

              {!loading && visibleCandidates.length > 0 && (
                <ul className="space-y-2" data-testid="admin-pending-non-div-list">
                  {visibleCandidates.map((c) => {
                    const isBusy = busyTicker === c.ticker;
                    return (
                      <li
                        key={c.ticker}
                        className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-border/60 bg-muted/10"
                        data-testid={`admin-pending-non-div-row-${c.ticker}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(c.ticker)}
                          onChange={() => toggleOne(c.ticker)}
                          disabled={bulkAccepting}
                          className="w-3.5 h-3.5 accent-purple-500 cursor-pointer shrink-0"
                          data-testid={`admin-pending-non-div-checkbox-${c.ticker}`}
                          aria-label={`Select ${c.ticker}`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            {/* Round 98p (Jul 2026) — ticker is now
                                clickable. Dispatches `open-stock-modal`
                                custom event listened for by Dashboard
                                (see Dashboard.js:1050) so the modal
                                opens IN-PLACE — no navigate, no page
                                reload, admin panel stays exactly where
                                it was. Closing the modal returns the
                                user to this same list, scroll position
                                and search filter preserved. */}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!c.ticker) return;
                                window.dispatchEvent(new CustomEvent('open-stock-modal', {
                                  detail: { ticker: String(c.ticker).toUpperCase() },
                                }));
                              }}
                              className="font-mono font-bold text-sm text-purple-200 hover:text-purple-100 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-500/40 rounded-sm"
                              title={`Open ${c.ticker} in the stock modal`}
                              data-testid={`admin-pending-non-div-ticker-${c.ticker}`}
                            >
                              <RecycledChevron ticker={c.ticker} />
                              {c.ticker}
                            </button>
                            <span className="text-xs text-muted-foreground truncate">
                              {c.name || '—'}
                            </span>
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-2">
                            <span>mcap: {fmtMcap(c.market_cap)}</span>
                            <span>·</span>
                            <span>{c.sector || 'no sector'}</span>
                            {c.primary_exchange && (
                              <>
                                <span>·</span>
                                <span>{c.primary_exchange}</span>
                              </>
                            )}
                            <span>·</span>
                            <span>found {fmtDate(c.discovered_at)}</span>
                            {c.has_massive_financials === false && (
                              <>
                                <span>·</span>
                                <span className="text-amber-400 font-semibold">
                                  ⚠ no Massive financials
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => accept(c.ticker)}
                            disabled={isBusy}
                            data-testid={`admin-pending-non-div-accept-${c.ticker}`}
                            title="Hydrate into stock_cache as a non-dividend row"
                            className="inline-flex items-center justify-center w-7 h-7 rounded bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 disabled:opacity-50"
                          >
                            {isBusy ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => moveToDividend(c.ticker)}
                            disabled={isBusy}
                            data-testid={`admin-pending-non-div-move-${c.ticker}`}
                            title="Move to Pending Dividend Review queue (if it actually pays a dividend)"
                            className="inline-flex items-center justify-center w-7 h-7 rounded bg-sky-500/15 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 disabled:opacity-50"
                          >
                            <ArrowRightLeft className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => trash(c.ticker)}
                            disabled={isBusy}
                            data-testid={`admin-pending-non-div-trash-${c.ticker}`}
                            title="Hard delete from queue (will re-appear next cycle if still classified as non-div)"
                            className="inline-flex items-center justify-center w-7 h-7 rounded bg-rose-500/15 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {!loading &&
                visibleCandidates.length === 0 &&
                report?.count > 0 && (
                  <div className="py-6 text-center text-xs text-muted-foreground">
                    No tickers match &ldquo;{searchQuery}&rdquo;
                  </div>
                )}
            </div>

            <div className="border-t border-border/60 px-4 py-2 text-[10px] text-muted-foreground bg-muted/20 shrink-0 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded bg-emerald-500/60" /> Accept =
                hydrate as non-div
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded bg-sky-500/60" /> Move = goes
                to Pending Div Review
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded bg-rose-500/60" /> Trash = hard
                delete
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
